// /admin/storage — document storage health + one-click cleanup of
// "orphaned" files (objects in R2 with no matching document record).
//
// Orphans only arise from a rare storage glitch during a delete — the
// normal delete path removes the file before its DB record. This page
// surfaces the reconciliation tool (worker: GET/POST /admin/api/r2-orphans)
// so the office can check + clean without a developer.

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { RefreshCw, Trash2, HardDrive, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { getR2Orphans, purgeR2Orphans } from '../../api/admin.js';
import { Button } from '../../components/ui/button.js';
import { ConfirmDialog } from '../../components/ConfirmDialog.js';

function Stat({ label, value, tone }: { label: string; value: number; tone?: 'warn' }) {
  return (
    <div className="rounded-lg border border-border bg-white p-4">
      <div
        className={
          'text-2xl font-semibold ' +
          (tone === 'warn' && value > 0 ? 'text-destructive' : 'text-foreground')
        }
      >
        {value}
      </div>
      <div className="mt-0.5 text-xs text-muted-foreground">{label}</div>
    </div>
  );
}

export function StoragePage() {
  const queryClient = useQueryClient();
  const [confirmOpen, setConfirmOpen] = useState(false);

  const { data, isLoading, isFetching, error, refetch } = useQuery({
    queryKey: ['admin', 'r2-orphans'],
    queryFn: getR2Orphans,
  });

  const purge = useMutation({
    mutationFn: purgeR2Orphans,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'r2-orphans'] });
    },
  });

  const orphans = data?.orphanObjects.length ?? 0;
  const missing = data?.missingObjects.length ?? 0;

  return (
    <div className="mx-auto max-w-3xl px-6 py-6">
      <div className="mb-4 flex items-center justify-between">
        <h1 className="flex items-center gap-2 text-lg font-semibold text-foreground">
          <HardDrive className="h-5 w-5 text-taupe" />
          Document Storage
        </h1>
        <Button
          variant="outline"
          onClick={() => refetch()}
          disabled={isFetching}
          className="gap-1.5"
        >
          <RefreshCw className={'h-3.5 w-3.5' + (isFetching ? ' animate-spin' : '')} />
          {isFetching ? 'Checking…' : 'Re-check'}
        </Button>
      </div>

      <p className="mb-4 text-sm text-muted-foreground">
        Every job document is stored as a file plus a record that links it to a job. They normally
        stay in sync. Rarely, a storage glitch during a delete can leave a leftover file that no
        document points to — harmless, but it wastes a little space. Use this page to find and
        clean those up.
      </p>

      {error && (
        <div className="mb-4 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
          {error instanceof Error ? error.message : 'Failed to load storage report.'}
        </div>
      )}

      {isLoading ? (
        <div className="text-sm text-muted-foreground">Checking storage…</div>
      ) : data ? (
        <>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <Stat label="Files in storage" value={data.r2Count} />
            <Stat label="Document records" value={data.dbCount} />
            <Stat label="Leftover files" value={orphans} tone="warn" />
            <Stat label="Missing files" value={missing} tone="warn" />
          </div>

          <div className="mt-5">
            {orphans === 0 ? (
              <div className="flex items-center gap-2 rounded-md border border-border bg-secondary/20 px-3 py-2.5 text-sm text-foreground">
                <CheckCircle2 className="h-4 w-4 text-primary" />
                No leftover files. Storage is clean.
              </div>
            ) : (
              <div className="flex flex-wrap items-center gap-3 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2.5">
                <AlertTriangle className="h-4 w-4 shrink-0 text-destructive" />
                <span className="text-sm text-foreground">
                  {orphans} leftover {orphans === 1 ? 'file' : 'files'} found.
                </span>
                <Button
                  variant="destructive"
                  className="ml-auto gap-1.5"
                  onClick={() => setConfirmOpen(true)}
                  disabled={purge.isPending}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                  Clean up {orphans === 1 ? 'file' : 'files'}
                </Button>
              </div>
            )}
          </div>

          {missing > 0 && (
            <div className="mt-3 rounded-md border border-border bg-white px-3 py-2.5 text-xs text-muted-foreground">
              {missing} document {missing === 1 ? 'record points' : 'records point'} to a file that
              is missing from storage. These can't be auto-fixed here — the original file is gone;
              the document may need to be deleted and re-uploaded.
            </div>
          )}

          {purge.isSuccess && (
            <div className="mt-3 text-xs text-muted-foreground">
              Cleaned up {purge.data.purged} leftover {purge.data.purged === 1 ? 'file' : 'files'}.
            </div>
          )}
          {purge.error && (
            <div className="mt-3 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">
              {purge.error instanceof Error ? purge.error.message : 'Cleanup failed.'}
            </div>
          )}
        </>
      ) : null}

      <ConfirmDialog
        open={confirmOpen}
        title="Clean up leftover files?"
        description={`This permanently removes ${orphans} file${
          orphans === 1 ? '' : 's'
        } from storage that no document points to. Document records are not affected.`}
        confirmLabel="Clean up"
        variant="destructive"
        isPending={purge.isPending}
        error={purge.error instanceof Error ? purge.error.message : null}
        onConfirm={() => {
          purge.mutate(undefined, { onSettled: () => setConfirmOpen(false) });
        }}
        onCancel={() => setConfirmOpen(false)}
      />
    </div>
  );
}
